import React, { useState, useEffect, useRef } from 'react';
import { parseServerTime } from '@/lib/serverTime';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { useAuth } from '@/contexts/AuthContext';
import { PageContainer, PageHeader } from '@/components/layout';
import { LoadingBlock, Spinner } from '@/components/ui/spinner';
import { PageTitle } from '@/components/common/PageTitle';
import { Search, Image as ImageIcon, Loader2, CheckCircle2, Clock, AlertCircle, Trash2, Globe, FileQuestion, Copy, Check, HelpCircle, Info, FileDown, RefreshCw, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { convertImageToBase64Smart, resizeImageBase64, KakaoCDNBlockedError } from '@/lib/imageUtils';
import { sseService } from '@/services/sseService';
import { AiUsageStatus } from '@/api/dashboardApi';
import { CustomAltEditor } from '@/components/custom-alt/CustomAltEditor';

// 날짜 유틸리티
const today = () => new Date().toISOString().slice(0, 10);
const oneMonthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

// 타입 선언부
type AltGenerationType = 'AI' | 'FALLBACK' | 'PENDING' | 'ERROR' | 'SKIPPED_DECORATIVE';

interface ImageInfo {
  id: number;
  url: string;
  imageSrc: string;
  displayImageUrl?: string;
  sourceType?: string;
  originalAlt: string | null;   // HTML에 원래 있던 alt 값 (null=속성 없음, ""=의도적 빈값, "텍스트"=기존 값)
  generatedAlt: string | null;
  altGenerationType: AltGenerationType;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  updatedAt?: string;
  cachecnt?: number;  // 캐시 사용 횟수
  customAltText: string | null;  // 커스텀 ALT 텍스트
  altApprovalStatus?: string;    // 팀 ALT 승인 워크플로우 상태 (PENDING/APPROVED/REJECTED), 해당없으면 undefined
}

interface ScanStatistics {
  totalImagesFound: number;    // 전체 발견 이미지
  imagesWithAlt: number;       // ALT 태그 이미 보유
  svgImagesFiltered: number;   // SVG 장식 이미지
  sameSrcDuplicate: number;    // 동일 URL 중복 태그 (같은 페이지 내 반복 src)
  duplicateFiltered: number;   // 하위 호환 (sameSrcDuplicate 동일 값)
  invalidUrlFiltered: number;  // 처리 제외 URL (data:, 비이미지 등)
  imagesWithoutAlt: number;    // ALT 없는 이미지 (신규 저장 대상)
}

interface ScanResult {
  success: boolean;
  scannedUrl: string;
  crawlMode: 'SINGLE_PAGE' | 'DOMAIN_WIDE';
  totalPages: number;
  totalImages: number;
  newImages: number;
  duplicateSkipped: number;    // 이전 스캔 DB 중복 (진짜 DB 중복)
  message: string;
  statistics?: ScanStatistics; // 상세 통계 (신규 필드)
  images?: ImageInfo[];        // 이번 스캔 이미지 (백엔드 응답)
}

type SortBy = 'CREATED_AT' | 'UPDATED_AT' | 'STATUS' | 'URL_NORMALIZED' | 'COMPLETION';
type SortDir = 'ASC' | 'DESC';
interface SortMeta { sortBy: SortBy; sortDir: SortDir; isDefault: boolean; }

interface PageResponse {
  content: ImageInfo[];
  totalPages: number;
  totalElements: number;
  number: number;
  sort?: SortMeta;
  stats?: {
    ai: number;
    fallback: number;
    pending: number;
    error: number;
    skippedDecorative: number;
    total: number;
    aiPercentage: number;
  };
}

const API_BASE_URL = `${BASE}/api/crawler`;

/**
 * 이미지 오류 유형 분류
 * - CDN_BLOCKED: 이미지는 존재하지만 CDN 보안 정책으로 AI 서버 접근 차단 (예: Kakao Signed URL)
 * - IMAGE_NOT_FOUND: 이미지 자체가 존재하지 않거나 깨진 경우
 * - OTHER: 그 외 오류
 */
function classifyImageError(generatedAlt: string | null | undefined, imageSrc?: string | null): 'CDN_BLOCKED' | 'IMAGE_NOT_FOUND' | 'OTHER' {
  if (!generatedAlt) return 'OTHER';
  const hasCdnKeyword =
    generatedAlt.includes('Kakao CDN') ||
    generatedAlt.includes('Signed URL') ||
    generatedAlt.includes('CDN_ACCESS_DENIED') ||
    generatedAlt.includes('KAKAO_CDN_SIGNED_URL') ||
    generatedAlt.includes('원본 사이트에서만 접근');
  const hasSignedUrlParams =
    !!imageSrc && imageSrc.includes('credential=') && imageSrc.includes('signature=');
  if (hasCdnKeyword || hasSignedUrlParams) return 'CDN_BLOCKED';
  if (generatedAlt.includes('이미지를 불러올 수 없습니다') || generatedAlt.includes('다운로드 실패')) return 'IMAGE_NOT_FOUND';
  return 'OTHER';
}

// 우선순위 정책: Custom Alt > Generated Alt > Original Alt
function getDisplayAlt(img: ImageInfo) {
  if (img.customAltText) return { text: img.customAltText, type: 'CUSTOM' as const };
  if (img.generatedAlt) return { text: img.generatedAlt, type: 'GENERATED' as const };
  if (img.originalAlt) return { text: img.originalAlt, type: 'ORIGINAL' as const };
  return { text: null, type: 'NONE' as const };
}



// Axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 인증 헤더 자동 추가 (interceptor)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    const activeTeamId = sessionStorage.getItem('activeTeamId');
    if (activeTeamId && config.headers) {
      config.headers['X-Team-Id'] = activeTeamId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터: 401 에러 처리
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/** blob → canvas 경유 PNG ArrayBuffer 변환 (crossOrigin 허용 여부와 무관하게 tainted canvas 가능성 있음) */
function blobToPngBuffer(
  blob: Blob
): Promise<{ buffer: ArrayBuffer; extension: 'png' } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 100;
      canvas.height = img.naturalHeight || 100;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0);
      try {
        canvas.toBlob(
          (pngBlob) => {
            if (!pngBlob) { resolve(null); return; }
            pngBlob.arrayBuffer()
              .then(buf => resolve({ buffer: buf, extension: 'png' }))
              .catch(() => resolve(null));
          },
          'image/png'
        );
      } catch {
        resolve(null); // tainted canvas
      }
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
    img.src = objectUrl;
  });
}

/** URL → canvas 경유 PNG ArrayBuffer (crossOrigin='anonymous', CORS 허용 CDN까지 커버) */
function imgToPngBuffer(
  url: string
): Promise<{ buffer: ArrayBuffer; extension: 'png' } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 100;
      canvas.height = img.naturalHeight || 100;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0);
      try {
        canvas.toBlob(
          (pngBlob) => {
            if (!pngBlob) { resolve(null); return; }
            pngBlob.arrayBuffer()
              .then(buf => resolve({ buffer: buf, extension: 'png' }))
              .catch(() => resolve(null));
          },
          'image/png'
        );
      } catch {
        resolve(null); // tainted canvas (CORS 미허용)
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function fetchImageForExcel(
  url: string,
  headers: HeadersInit = {}
): Promise<{ buffer: ArrayBuffer; extension: 'jpeg' | 'png' | 'gif' } | null> {
  // 전략 1: 직접 fetch (동일 출처 또는 CORS 허용 CDN)
  try {
    const res = await fetch(url, { headers });
    if (res.ok) {
      const blob = await res.blob();
      const type = blob.type.toLowerCase();
      if (type === 'image/png') return { buffer: await blob.arrayBuffer(), extension: 'png' };
      if (type.includes('jpeg') || type.includes('jpg')) return { buffer: await blob.arrayBuffer(), extension: 'jpeg' };
      if (type === 'image/gif') return { buffer: await blob.arrayBuffer(), extension: 'gif' };
      // webp, bmp 등 → canvas로 PNG 변환
      return await blobToPngBuffer(blob);
    }
  } catch {
    // CORS 차단 또는 네트워크 에러 → 전략 2로 낙하
  }

  // 전략 2: <img crossOrigin='anonymous'> + canvas
  // fetch와 달리 브라우저 이미지 캐시를 활용하므로 일부 CDN에서 추가 성공 가능
  const v2 = await imgToPngBuffer(url);
  if (v2) return v2;

  // 전략 3: 백엔드 프록시 경유
  // Daum/Kakao CDN 등 CORS 헤더 없는 외부 이미지를 서버가 fetch해서 반환
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const proxyUrl = `${BASE}/api/crawler/image-proxy?url=${encodeURIComponent(url)}`;
    const proxyHeaders: HeadersInit = {};
    if (token) (proxyHeaders as Record<string, string>).Authorization = `Bearer ${token}`;
    const res = await fetch(proxyUrl, { headers: proxyHeaders });
    if (res.ok) {
      const blob = await res.blob();
      const type = blob.type.toLowerCase();
      if (type === 'image/png') return { buffer: await blob.arrayBuffer(), extension: 'png' };
      if (type.includes('jpeg') || type.includes('jpg')) return { buffer: await blob.arrayBuffer(), extension: 'jpeg' };
      if (type === 'image/gif') return { buffer: await blob.arrayBuffer(), extension: 'gif' };
      return await blobToPngBuffer(blob);
    }
  } catch {
    // 프록시 미구현 또는 에러 → null 반환
  }

  return null;
}

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = parseServerTime(value);
  if (!date || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR');
};

const formatRelativeTime = (value?: string): string => {
  if (!value) return '-';
  const date = parseServerTime(value);
  if (!date || Number.isNaN(date.getTime())) return '-';
  const diffSec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}초 전`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;
  const diffDay = Math.floor(diffHour / 24);
  return `${diffDay}일 전`;
};

function AlttextUrl() {
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';
  // ...useState declarations...

  // ALT 복사 함수
  const handleCopyAlt = async (text: string, imageId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(imageId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      alert('복사하지 못했습니다.');
    }
  };

  // HTML 복사 함수
  const handleCopyHtml = async (img: ImageInfo) => {
    try {
      const displayAlt = getDisplayAlt(img);
      const htmlTag = `<img src="${img.displayImageUrl ?? img.imageSrc}" alt="${displayAlt.text || ''}" />`;
      await navigator.clipboard.writeText(htmlTag);
      setCopiedHtmlId(img.id);
      setTimeout(() => setCopiedHtmlId(null), 2000);
    } catch (err) {
      alert('복사하지 못했습니다.');
    }
  };

  // 커스텀 ALT 저장 핸들러
  const handleCustomAltSaved = (imageId: number, customAltText: string) => {
    setImages(images.map(img => 
      img.id === imageId 
        ? { ...img, customAltText }
        : img
    ));
  };

  // 커스텀 ALT 삭제 핸들러
  const handleCustomAltDeleted = (imageId: number) => {
    setImages(images.map(img => 
      img.id === imageId 
        ? { ...img, customAltText: null }
        : img
    ));
  };
  const [url, setUrl] = useState('');
  const [originalUrl, setOriginalUrl] = useState('');
  const [crawlMode, setCrawlMode] = useState<'SINGLE_PAGE' | 'DOMAIN_WIDE'>('SINGLE_PAGE');
  const [maxDepth, setMaxDepth] = useState(10);
  const [maxPages, setMaxPages] = useState(100);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [allStats, setAllStats] = useState<{ ai: number; fallback: number; pending: number; error: number; skippedDecorative: number; total: number; aiPercentage: number } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'COMPLETED'>('ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'website'>('all');
  const [websites, setWebsites] = useState<string[]>([]);
  const [selectedWebsite, setSelectedWebsite] = useState<string>('');
  const [altTypeFilter, setAltTypeFilter] = useState<AltGenerationType | 'ALL'>('ALL');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [copiedHtmlId, setCopiedHtmlId] = useState<number | null>(null);
  const [regeneratingIds, setRegeneratingIds] = useState<Set<number>>(new Set());
  // 브라우저에서도 이미지 로드 실패한 ID 추적 (서버 접근 차단 vs 실제 없는 이미지 구분용)
  const [browserFailedImages, setBrowserFailedImages] = useState<Set<number>>(new Set());
  const [showGuide, setShowGuide] = useState(false);
  const [aiUsage, setAiUsage] = useState<AiUsageStatus | null>(null);
  const [useContextProfile, setUseContextProfile] = useState(false);
  const [profileActive, setProfileActive] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [tick, setTick] = useState(0); // PENDING 타임아웃 감지용 리렌더 트리거
  const [generatingProgress, setGeneratingProgress] = useState(false); // 생성 중 폴링 상태
  const [initialPendingCount, setInitialPendingCount] = useState(0); // 생성 시작 시 미생성 건수
  const [generationComplete, setGenerationComplete] = useState(false); // 생성 완료 여부
  const [sortBy, setSortBy] = useState<SortBy>('UPDATED_AT'); // 웹사이트 기본: 수정일 DESC
  const [sortDir, setSortDir] = useState<SortDir>('DESC');
  const [sortIsDefault, setSortIsDefault] = useState(true);
  const [startDate, setStartDate] = useState(oneMonthAgo());
  const [endDate, setEndDate] = useState(today());
  const generationPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAiUsage = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${BASE}/api/dashboard/ai-usage-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAiUsage(response.data);
    } catch (err) {
      // 사용량 조회 실패 시 무시
    }
  };

  const fetchProfileStatus = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(`${BASE}/api/user/alt-profiles/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const { hasProfile, useYn } = response.data;
      setProfileActive(hasProfile);
      setUseContextProfile(hasProfile && useYn === 'Y');
    } catch {
      // 프로필 상태 조회 실패 시 무시 (미등록으로 처리)
    }
  };


  // 이미지 삭제 함수
  const handleDeleteImage = async (imageId: number) => {
    if (!window.confirm('이 이미지를 삭제하시겠습니까?\n\n삭제된 이미지는 복구할 수 없습니다.')) {
      return;
    }
    try {
      await apiClient.delete(`/images/${imageId}`);
      fetchImages(currentPage, filter === 'ALL' ? null : filter, viewMode === 'website' ? selectedWebsite : null);
      alert('이미지가 삭제되었습니다.');
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('삭제 API가 구현되지 않았습니다. 백엔드 개발이 필요합니다.');
      } else {
        setError('이미지를 삭제하지 못했습니다.');
      }
    }
  };

  const handleDoubleClick = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setError('복사되었습니다!');
      setTimeout(() => setError(null), 1500);
    } catch (err) {
      // 복사 실패 시 무시
    }
  };

  const fetchStats = async (status: string | null = null, websiteUrl: string | null = null, altGenerationType: AltGenerationType | 'ALL' | null = null) => {
    try {
      const statusParam = status && status !== 'ALL' ? `&status=${status}` : '';
      const urlParam = websiteUrl ? `&url=${encodeURIComponent(websiteUrl)}` : '';
      const altTypeParam = altGenerationType && altGenerationType !== 'ALL' ? `&altGenerationType=${altGenerationType}` : '';
      const channelParam = '&channel=INNER'; // INNER 채널만 필터링
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await apiClient.get<PageResponse>(
        `/images?page=0&size=999999&sourceType=website${statusParam}${urlParam}${altTypeParam}${channelParam}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      // 백엔드 stats 필드 그대로 사용
      if (res.data.stats) {
        setAllStats(res.data.stats);
      }
    } catch {
      // 통계 조회 실패는 무시
    }
  };

  const handleExcelDownload = async () => {
    setExcelLoading(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const websiteUrl = viewMode === 'website' ? selectedWebsite : null;
      const statusParam = filter !== 'ALL' ? `&status=${filter}` : '';
      const urlParam = websiteUrl ? `&url=${encodeURIComponent(websiteUrl)}` : '';
      const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

      // 전체 페이지 순차 fetch (size=9999 대신 안정적인 배치 크기 사용)
      let allData: ImageInfo[] = [];
      let fetchPage = 0;
      while (true) {
        const batchRes = await apiClient.get<PageResponse>(
          `/images?page=${fetchPage}&size=999999&sourceType=website${statusParam}${urlParam}`,
          { headers: authHeaders }
        );
        allData = allData.concat(batchRes.data.content);
        if (fetchPage >= batchRes.data.totalPages - 1 || batchRes.data.content.length === 0) break;
        fetchPage++;
      }
      // 목록에 표시되는 것과 동일하게 altTypeFilter 적용
      const data = altTypeFilter === 'ALL' ? allData : allData.filter(img => img.altGenerationType === altTypeFilter);

      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('이미지목록');
      ws.columns = [
        { header: 'ID', key: 'id', width: 8 },
        { header: '페이지 URL', key: 'pageUrl', width: 45 },
        { header: '이미지 URL', key: 'imageUrl', width: 55 },
        { header: '기존 ALT', key: 'origAlt', width: 30 },
        { header: 'AI 생성 ALT', key: 'alt', width: 50 },
        { header: '생성 방식', key: 'type', width: 15 },
        { header: '상태', key: 'status', width: 12 },
        { header: '최종 수정일', key: 'updatedAt', width: 22 },
      ];
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).height = 18;

      for (let i = 0; i < data.length; i++) {
        const img = data[i];
        const imgUrl = img.displayImageUrl || img.imageSrc || '';
        const row = ws.addRow({
          id: img.id,
          pageUrl: img.url ?? '',
          imageUrl: imgUrl,
          origAlt: img.originalAlt ?? '',
          alt: img.generatedAlt ?? '',
          type: img.altGenerationType ?? '',
          status: img.status ?? '',
          updatedAt: parseServerTime(img.updatedAt || img.createdAt)?.toLocaleString('ko-KR') ?? '-',
        });
        if (imgUrl) {
          row.getCell('imageUrl').value = { text: imgUrl, hyperlink: imgUrl };
          row.getCell('imageUrl').font = { color: { argb: 'FF0563C1' }, underline: true };
        }
      }

      const buf = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `웹사이트크롤링_이미지목록_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch {
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setExcelLoading(false);
    }
  };

  const fetchWebsites = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await apiClient.get<string[]>('/websites', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setWebsites(response.data);
    } catch (err) {
      setError('웹사이트 목록을 불러오지 못했습니다.');
    }
  };

  const fetchImages = async (page = 0, status: string | null = null, websiteUrl: string | null = null, size = pageSize, sb?: SortBy, sd?: SortDir, altGenerationType?: AltGenerationType | 'ALL' | null, overrideStartDate?: string, overrideEndDate?: string) => {
    setLoading(true);
    try {
      const effectiveSortBy = sb ?? sortBy;
      const effectiveSortDir = sd ?? sortDir;
      const statusParam = status && status !== 'ALL' ? `&status=${status}` : '';
      const urlParam = websiteUrl ? `&url=${encodeURIComponent(websiteUrl)}` : '';
      const effectiveAltType = altGenerationType ?? altTypeFilter;
      const altTypeParam = effectiveAltType && effectiveAltType !== 'ALL' ? `&altGenerationType=${effectiveAltType}` : '';
      const sourceTypeParam = '&sourceType=website'; // 웹사이트 크롤링만 필터링
      const channelParam = '&channel=INNER'; // INNER 채널만 필터링
      const sortParam = `&sortBy=${effectiveSortBy}&sortDir=${effectiveSortDir}`;
      const effectiveStartDate = overrideStartDate ?? startDate;
      const effectiveEndDate = overrideEndDate ?? endDate;
      const startDateParam = effectiveStartDate ? `&startDate=${effectiveStartDate}` : '';
      const endDateParam = effectiveEndDate ? `&endDate=${effectiveEndDate}` : '';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await apiClient.get<PageResponse>(
        `/images?page=${page}&size=${size}${statusParam}${urlParam}${altTypeParam}${sourceTypeParam}${channelParam}${sortParam}${startDateParam}${endDateParam}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setImages(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements ?? 0);
      setCurrentPage(page);
      if (response.data.sort) setSortIsDefault(response.data.sort.isDefault);
      // fetchImages 응답의 stats 재사용 (불필요한 API 호출 제거)
      if (response.data.stats) {
        setAllStats(response.data.stats);
      }
    } catch (err: any) {
      // 네트워크 오류
      if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        setError('백엔드 서버에 연결할 수 없습니다. 서버가 실행 중인지 확인해주세요.');
      }
      // 404 또는 빈 결과는 에러가 아님
      else if (err.response?.status === 404 || err.response?.data?.content?.length === 0) {
        setImages([]);
        setTotalPages(0);
        setError(null);
      }
      // 기타 오류
      else if (err.response?.data?.message) {
        setError(`이미지 목록을 불러오지 못했습니다: ${err.response.data.message}`);
      }
      else {
        setError('이미지 목록을 불러오지 못했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebsites();
    fetchImages(0, null, null); // 초기 로드 시 전체 이미지 목록 조회
    fetchAiUsage();
    fetchProfileStatus();
  }, []);

  // PENDING 항목이 30초 경과 후 타임아웃 UI로 전환되도록 리렌더 트리거
  useEffect(() => {
    const hasFreshPending = images.some(img => !img.generatedAlt);
    if (!hasFreshPending) return;
    const timer = setTimeout(() => setTick(t => t + 1), 35_000);
    return () => clearTimeout(timer);
  }, [images]);

  // 이미지가 생성된 지 30초 이상 경과하여 PENDING 상태이면 true
  const isLongPending = (createdAt: string): boolean => {
    try {
      const parsed = parseServerTime(createdAt);
      return parsed ? Date.now() - parsed.getTime() > 30_000 : false;
    } catch { return false; }
  };

  useEffect(() => {
    if (crawlMode === 'DOMAIN_WIDE' && url) {
      const domain = extractDomain(url);
      setUrl(domain);
    } else if (crawlMode === 'SINGLE_PAGE' && originalUrl) {
      setUrl(originalUrl);
    }
  }, [crawlMode]);

  // 도메인 추출 함수
  const extractDomain = (inputUrl: string): string => {
    try {
      const urlObj = new URL(inputUrl);
      return `${urlObj.protocol}//${urlObj.host}`;
    } catch (error) {
      return inputUrl;
    }
  };

  // URL 입력 핸들러
  const handleUrlChange = (inputUrl: string) => {
    setOriginalUrl(inputUrl);
    
    if (crawlMode === 'DOMAIN_WIDE') {
      setUrl(extractDomain(inputUrl));
    } else {
      setUrl(inputUrl);
    }
  };

  // 프리셋 적용
  const applyPreset = (preset: 'shallow' | 'normal' | 'deep') => {
    switch (preset) {
      case 'shallow':
        setMaxDepth(5);
        setMaxPages(50);
        break;
      case 'normal':
        setMaxDepth(10);
        setMaxPages(100);
        break;
      case 'deep':
        setMaxDepth(20);
        setMaxPages(200);
        break;
    }
  };

  // 이미지 URL 여부 감지 (확장자 기반)
  const isImageUrl = (inputUrl: string): boolean => {
    try {
      const pathname = new URL(inputUrl).pathname.toLowerCase().split('?')[0];
      return /\.(jpe?g|jpg|jfif|png|gif|webp|svg|avif|tiff?|bmp|ico)$/i.test(pathname);
    } catch {
      return false;
    }
  };

  const handleScan = async () => {
    if (!url.trim()) {
      setError('URL을 입력해주세요.');
      return;
    }

    // 이미지 URL 직접 입력 감지 → 이미지 URL 페이지로 안내
    if (isImageUrl(url.trim())) {
      const goToImageUrl = window.confirm(
        '입력하신 주소는 이미지 파일 URL입니다.\n\n웹사이트 크롤링은 웹페이지 주소(https://example.com)를 입력해야 합니다.\n\n[확인] → 이미지 URL 입력 화면으로 이동하여 처리\n[취소] → 현재 화면 유지'
      );
      if (goToImageUrl) {
        navigate('/dashboard/crawler/image-url', { state: { prefillUrl: url.trim() } });
      }
      return;
    }

    // 검증
    if (crawlMode === 'DOMAIN_WIDE') {
      if (maxDepth < 1 || maxDepth > 20) {
        setError('깊이 제한은 1~20 사이여야 합니다.');
        return;
      }
      if (maxPages < 1 || maxPages > 200) {
        setError('방문 수 제한은 1~200 사이여야 합니다.');
        return;
      }
    }

    if (aiUsage) {
      // 팀 컨텍스트인 경우 팀 한도 기준으로 체크
      if (aiUsage.isTeamContext) {
        const teamLimit = aiUsage.teamLimit ?? aiUsage.limit;
        const teamUsed = aiUsage.teamTotalUsed ?? aiUsage.used;
        if (teamUsed >= teamLimit) {
          setError(`팀 AI 사용 한도(${teamLimit}건)를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.`);
          return;
        }
      } else if (!aiUsage.canUse) {
        setError(`이번 달 AI 사용 한도(${aiUsage.limit}건)를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.`);
        return;
      }
    }

    // FREE 플랜 일일 한도 초과 체크
    if (aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0)) {
      setError(`무료 플랜은 하루 최대 ${aiUsage.dailyLimit}건의 ALT 텍스트를 생성할 수 있습니다. 내일 다시 시도하거나 스타트 플랜 이상으로 업그레이드해 주세요.`);
      return;
    }

    setScanning(true);
    setScanResult(null);
    setError(null);

    try {
      const response = await apiClient.post<ScanResult>(
        '/scan',
        {
          url: url,
          crawlMode: crawlMode,
          maxDepth: crawlMode === 'DOMAIN_WIDE' ? maxDepth : undefined,
          maxPages: crawlMode === 'DOMAIN_WIDE' ? maxPages : undefined,
          useContextProfile: useContextProfile
        },
        {
          timeout: 30 * 60 * 1000 // 30분 타임아웃
        }
      );
      
      setScanResult(response.data);
      fetchWebsites();
      fetchAiUsage(); // 스캔 후 사용량 즉시 갱신

      // 스캔 완료 후: 이번 스캔 이미지 표시 (백엔드 응답의 images 배열 사용)
      // ✅ 이전 상태/유형 탭(altTypeFilter)이 ALL이 아니면 방금 스캔한 결과가 그
      // 필터 조건과 안 맞아 목록에서 안 보이는 문제가 있었다 - 새로 처리한 결과는
      // 필터와 무관하게 항상 보이도록 두 탭을 ALL로 되돌린다.
      setAltTypeFilter('ALL');
      if (response.data.images && response.data.images.length > 0) {
        setViewMode('all');
        setFilter('ALL');
        setImages(response.data.images);
        setTotalElements(response.data.images.length);
        setTotalPages(1);
        setCurrentPage(0);
      } else {
        // images가 없으면 기존처럼 URL 필터로 조회
        const scannedUrl = response.data.scannedUrl;
        setViewMode('website');
        setSelectedWebsite(scannedUrl);
        setFilter('ALL');
        fetchImages(0, null, scannedUrl, pageSize, undefined, undefined, 'ALL');
      }
    } catch (err: any) {
      if (err.code === 'ECONNABORTED') {
        setError('스캔 시간이 초과되었습니다. 범위를 줄여서 다시 시도해주세요.');
      } else if (err.response?.status === 429) {
        if (aiUsage?.plan === 'FREE') {
          alert(`무료 플랜은 하루 최대 20개의 ALT 텍스트를 생성할 수 있습니다.\n제한 없이 이용하시려면 스타트 플랜 이상으로 업그레이드해 주세요.`);
        } else {
          setError(err.response?.data?.message || '이번 달 AI 사용 한도를 초과하였습니다. 플랜 업그레이드 또는 관리자에게 문의해 주세요.');
        }
        fetchAiUsage();
      } else if (err.response?.status === 502 || err.response?.status === 504) {
        setError('서버 게이트웨이 타임아웃이 발생했습니다. 백엔드는 크롤링을 계속 진행 중일 수 있으니 잠시 후 이미지 목록을 확인해 주세요.');
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        setError('연결이 끊어졌습니다. 서버는 크롤링을 계속 진행 중일 수 있으니 잠시 후 이미지 목록을 새로고침해 주세요.');
      } else {
        setError(err.response?.data?.message || '크롤링 중 오류가 발생했습니다.');
      }
    } finally {
      setScanning(false);
    }
  };

  const handleGenerateAlt = async () => {
    if (!window.confirm('AI가 대기중인 이미지를 자동으로 처리합니다. 생성 진행 상황을 모니터링할까요?')) {
      return;
    }

    setGenerating(true);
    setError(null);
    setGenerationComplete(false);

    // 현재 미생성 건수 저장
    const currentPending = allStats?.pending ?? 0;
    setInitialPendingCount(currentPending);

    try {
      // Spring Batch가 자동으로 PENDING 이미지를 처리 중 → 별도 트리거 불필요
      // 실시간 폴링으로 진행 상황 모니터링
      setGeneratingProgress(true);
      const websiteFilter = viewMode === 'website' ? selectedWebsite : null;

      // 기존 폴링이 있으면 제거
      if (generationPollingRef.current) clearInterval(generationPollingRef.current);

      // 5초 간격으로 통계 갱신
      generationPollingRef.current = setInterval(async () => {
        try {
          const statusParam = '';
          const urlParam = websiteFilter ? `&url=${encodeURIComponent(websiteFilter)}` : '';
          const res = await apiClient.get<PageResponse>(
            `/images?page=0&size=999999&sourceType=website${statusParam}${urlParam}`
          );
          const all = res.data.content;
          const newStats = {
            ai: all.filter(i => i.altGenerationType === 'AI').length,
            fallback: all.filter(i => i.altGenerationType === 'FALLBACK').length,
            pending: all.filter(i => i.altGenerationType === 'PENDING').length,
            error: all.filter(i => i.altGenerationType === 'ERROR').length,
            skippedDecorative: all.filter(i => i.altGenerationType === 'SKIPPED_DECORATIVE').length,
            total: all.length,
            aiPercentage: all.length > 0 ? Math.round((all.filter(i => i.altGenerationType === 'AI').length / all.length) * 100) : 0,
          };
          setAllStats(newStats);

          // 미생성 0건 → 완료
          if (newStats.pending === 0) {
            if (generationPollingRef.current) clearInterval(generationPollingRef.current);
            generationPollingRef.current = null;
            setGeneratingProgress(false);
            setGenerationComplete(true);
            setGenerating(false);
            fetchImages(currentPage, filter === 'ALL' ? null : filter, websiteFilter);
            fetchAiUsage();
          }
        } catch {
          // 폴링 실패는 무시
        }
      }, 5000);

      // 최대 10분 후 폴링 자동 종료
      setTimeout(() => {
        if (generationPollingRef.current) {
          clearInterval(generationPollingRef.current);
          generationPollingRef.current = null;
          setGeneratingProgress(false);
          setGenerating(false);
          fetchImages(currentPage, filter === 'ALL' ? null : filter, websiteFilter);
        }
      }, 10 * 60 * 1000);

    } catch (err: any) {
      setGeneratingProgress(false);
      if (err.response?.status === 429) {
        const msg = err.response?.data?.message || '무료 플랜은 하루 ALT 생성이 최대 20건으로 제한됩니다. 제한 없이 이용하시려면 스타트 플랜 이상으로 업그레이드해 주세요.';
        alert(msg);
        fetchAiUsage();
      } else {
        setError(err.response?.data?.message || err.response?.data || 'ALT 생성 중 오류가 발생했습니다.');
      }
      setGenerating(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('모든 이미지 데이터를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await apiClient.delete('/images/clear?sourceType=website');
    } catch (err) {
      setError('데이터를 삭제하지 못했습니다.');
    }
  };

  const handleClearWebsite = async () => {
    if (!selectedWebsite) {
      alert('웹사이트를 선택하세요');
      return;
    }

    if (!window.confirm(`"${selectedWebsite}"의 모든 이미지 데이터를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const urlParam = `?url=${encodeURIComponent(selectedWebsite)}`;
      await apiClient.delete(`/images/clear${urlParam}`);
      
      // 삭제 후 목록 새로고침
      const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
      fetchImages(0, filter === 'ALL' ? null : filter, websiteFilter);
      fetchWebsites();
      
      setError(null);
      alert(`"${selectedWebsite}"의 데이터가 삭제되었습니다.`);
    } catch (err) {
      setError('데이터를 삭제하지 못했습니다.');
    }
  };

  const handleSearch = () => {
    if (viewMode === 'website' && !selectedWebsite) {
      alert('웹페이지를 선택하세요');
      return;
    }

    const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
    const statusFilter = filter === 'ALL' ? null : filter;
    fetchImages(0, statusFilter, websiteFilter, pageSize, undefined, undefined, undefined, startDate, endDate);
  };

  const handleRegenerateAlt = async (imageId: number, forceRefresh = false) => {
    if (aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0)) {
      setError(`⚠️ 오늘 ${aiUsage.dailyUsed ?? 0}/${aiUsage.dailyLimit}건 — 일일 한도 초과. 내일 다시 이용하거나 플랜을 업그레이드하세요.`);
      return;
    }
    
    // forceRefresh인 경우 새로운 엔드포인트 사용
    if (forceRefresh) {
      if (!window.confirm('이미 AI로 생성한 ALT를 다시 생성하여 ALT를 업데이트 하시겠습니까? (AI 사용량이 차감됩니다)')) {
        return;
      }

      setRegeneratingIds(prev => new Set(prev).add(imageId));
      try {
        const response = await apiClient.post(`/images/${imageId}/force-refresh`);

        // SSE 연결로 실시간 상태 수신
        sseService.connect(imageId, {
          onConnected: (data) => {
            console.log('[SSE] 강제 재분석 연결됨:', data);
          },
          onStatusUpdate: (data) => {
            if (data.status === 'FAILED' && data.errorType) {
              // 에러 코드에 따른 알림 표시
              let errorMessage = '이미지 분석에 실패했습니다.';
              if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
                errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
              } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
                errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
              } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
                errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
              }
              alert(errorMessage);
            }

            if (data.status === 'COMPLETED' || data.status === 'FAILED') {
              const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
              fetchImages(currentPage, filter === 'ALL' ? null : filter, websiteFilter);
              fetchAiUsage();
              setRegeneratingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(imageId);
                return newSet;
              });
              sseService.disconnect();
            }
          },
          onTimeout: (data) => {
            console.log('[SSE] 타임아웃:', data);
            const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
            fetchImages(currentPage, filter === 'ALL' ? null : filter, websiteFilter);
            fetchAiUsage();
            setRegeneratingIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(imageId);
              return newSet;
            });
          },
          onError: (error) => {
            console.error('[SSE] 에러:', error);
            const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
            fetchImages(currentPage, filter === 'ALL' ? null : filter, websiteFilter);
            fetchAiUsage();
            setRegeneratingIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(imageId);
              return newSet;
            });
          }
        });
      } catch (err: any) {
        alert(err.response?.data?.error || 'ALT 강제 재생성 실패');
        setRegeneratingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageId);
          return newSet;
        });
      }
      return;
    }
    
    // 기존 재분석 로직
    const confirmMessage = 'AI를 통한 ALT 생성을 다시 시도하시겠습니까?';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setRegeneratingIds(prev => new Set(prev).add(imageId));
    try {
      // 브라우저에서 Base64 변환 시도 (Kakao Signed URL 등 서버 다운로드 실패 대비)
      const image = images.find(i => i.id === imageId);
      const imageSrc = image?.displayImageUrl ?? image?.imageSrc ?? '';
      let imageBase64: string | null = null;
      if (imageSrc) {
        console.log('[재분석] Base64 변환 시도 (직접 + 프록시):', imageSrc);
        const pageReferer = image?.url || undefined;
        imageBase64 = await convertImageToBase64Smart(imageSrc, BASE, pageReferer);
        if (imageBase64 && imageBase64.length > 10_000_000) {
          imageBase64 = await resizeImageBase64(imageBase64, 1920, 1080, 0.85);
        }
        if (imageBase64) {
          console.log('[재분석] Base64 변환 성공');
        } else {
          console.warn('[재분석] Base64 변환 실패 → 서버 다운로드 fallback');
        }
      }
      // 백엔드 @RequestParam 수신: application/x-www-form-urlencoded 방식으로 전송
      const params = new URLSearchParams();
      params.append('useContextProfile', useContextProfile ? 'true' : 'false');
      if (imageBase64) params.append('imageBase64', imageBase64);
      const response = await apiClient.post(
        `/images/${imageId}/regenerate`,
        params.toString(),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      // SSE 연결로 실시간 상태 수신
      sseService.connect(imageId, {
        onConnected: (data) => {
          console.log('[SSE] 재분석 연결됨:', data);
        },
        onStatusUpdate: (data) => {
          if (data.status === 'FAILED' && data.errorType) {
            // 에러 코드에 따른 알림 표시
            let errorMessage = '이미지 분석에 실패했습니다.';
            if (data.errorType === 'MONTHLY_LIMIT_EXCEEDED') {
              errorMessage = '월별 한도를 초과했습니다. 요금제 업그레이드를 진행해 주세요.';
            } else if (data.errorType === 'DAILY_LIMIT_EXCEEDED') {
              errorMessage = '일별 한도를 초과했습니다. 내일 다시 시도해주세요.';
            } else if (data.errorType === 'ENTERPRISE_LIMIT_EXCEEDED') {
              errorMessage = '엔터프라이즈 한도를 초과했습니다. 엔터프라이즈 계약서 수정으로 이용가능한 건수를 추가해 주세요.';
            }
            alert(errorMessage);
          }

          if (data.status === 'COMPLETED' || data.status === 'FAILED') {
            const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
            fetchImages(currentPage, filter === 'ALL' ? null : filter, websiteFilter);
            setRegeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(imageId);
              return next;
            });
            sseService.disconnect();
          }
        },
        onTimeout: (data) => {
          console.log('[SSE] 타임아웃:', data);
          const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
          fetchImages(currentPage, filter === 'ALL' ? null : filter, websiteFilter);
          setRegeneratingIds(prev => {
            const next = new Set(prev);
            next.delete(imageId);
            return next;
          });
        },
        onError: (error) => {
          console.error('[SSE] 에러:', error);
          const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
          fetchImages(currentPage, filter === 'ALL' ? null : filter, websiteFilter);
          setRegeneratingIds(prev => {
            const next = new Set(prev);
            next.delete(imageId);
            return next;
          });
        }
      });
    } catch (err: any) {
      let msg = '재분석 중 오류가 발생했습니다.';
      if (err instanceof KakaoCDNBlockedError) {
        msg = `⚠️ Kakao CDN 이미지 분석 제한\n\n${err.message}`;
      } else if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          msg = err.response.data;
        } else if (typeof err.response.data.message === 'string') {
          msg = err.response.data.message;
        }
      }
      setError(msg);
      alert(msg);
      setRegeneratingIds(prev => {
        const next = new Set(prev);
        next.delete(imageId);
        return next;
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { className: string; icon: React.ElementType; label: string }> = {
      PENDING: { className: 'bg-gray-200 text-gray-800', icon: Clock, label: '대기중' },
      COMPLETED: { className: 'bg-green-200 text-green-800', icon: CheckCircle2, label: '완료' },
      FAILED: { className: 'bg-red-200 text-red-800', icon: AlertCircle, label: '오류' }
    };

    const item = config[status] ?? { className: 'bg-gray-200 text-gray-800', icon: Clock, label: status || '대기중' };
    const { className, icon: Icon, label } = item;

    return (
      <Badge className={`flex items-center gap-1 w-fit ${className}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getAltTypeBadge = (type: AltGenerationType) => {
    const config = {
      AI: { className: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2, label: '✓ AI 분석' },
      FALLBACK: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertCircle, label: '⚠ 임시 생성' },
      ERROR: { className: 'bg-red-100 text-red-800 border-red-300', icon: AlertCircle, label: '✗ 생성 오류' },
      PENDING: { className: generatingProgress ? 'bg-blue-100 text-blue-800 border-blue-300 animate-pulse' : 'bg-gray-100 text-gray-800 border-gray-300', icon: generatingProgress ? Loader2 : FileQuestion, label: generatingProgress ? '⏳ AI 생성중' : '📝 미생성' },
      SKIPPED_DECORATIVE: { className: 'bg-purple-100 text-purple-800 border-purple-300', icon: AlertCircle, label: '🎨 장식용 이미지' }
    };

    const entry = config[type] ?? { className: 'bg-gray-100 text-gray-800 border-gray-300', icon: AlertCircle, label: type ?? '알 수 없음' };
    const { className, icon: Icon, label } = entry;

    return (
      <Badge className={`flex items-center gap-1 w-fit border ${className}`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getAltTypeStats = () => {
    if (!allStats) {
      return { ai: 0, fallback: 0, pending: 0, error: 0, skippedDecorative: 0, total: 0, aiPercentage: 0 };
    }
    return allStats;
  };

  const filteredImages = images;

  // 스캔 결과 통계 메시지를 파싱하여 표 형식으로 렌더링
  const renderScanStats = (scanRes: ScanResult) => {
    const { statistics, newImages, duplicateSkipped, message } = scanRes;
    const lines = message.split('\n').filter(l => l.trim());
    const titleLine = lines[0];
    const bulletLines = lines.slice(1).filter(l => l.trimStart().startsWith('•'));

    type RowMeta = { label: string; actionable: boolean; actionLabel: string; actionCls: string; desc: string };
    const classify = (line: string): RowMeta => {
      if (line.includes('ALT 태그') || line.includes('이미 처리됨'))
        return { label: line.replace(/^\s*•\s*/, '').replace(/\s*\(.*\)\s*✓?/g, '').trim(), actionable: false, actionLabel: '생성 불필요', actionCls: 'bg-green-100 text-green-700', desc: '이미 ALT 태그가 존재합니다' };
      if (line.includes('SVG') || line.includes('ALT 불필요'))
        return { label: line.replace(/^\s*•\s*/, '').replace(/\s*\(.*\)\s*/g, '').trim(), actionable: false, actionLabel: '생성 불필요', actionCls: 'bg-slate-100 text-slate-500', desc: '장식용 SVG 등 ALT가 필요 없는 이미지' };
      if (line.includes('중복'))
        return { label: line.replace(/^\s*•\s*/, '').replace(/\s*\(.*\)\s*/g, '').trim(), actionable: false, actionLabel: '생성 대상 아님', actionCls: 'bg-yellow-100 text-yellow-700', desc: '이전 스캔에서 이미 저장된 이미지' };
      if (line.includes('제외'))
        return { label: line.replace(/^\s*•\s*/, '').replace(/\s*\(.*\)\s*/g, '').trim(), actionable: false, actionLabel: '생성 불가', actionCls: 'bg-gray-100 text-gray-500', desc: 'data: URL, 비이미지 확장자 등' };
      if (line.includes('전체 이미지'))
        return { label: line.replace(/^\s*•\s*/, '').trim(), actionable: true, actionLabel: '전체 발견', actionCls: 'bg-blue-100 text-blue-700', desc: '이번 스캔에서 발견된 전체 이미지' };
      return { label: line.replace(/^\s*•\s*/, '').trim(), actionable: true, actionLabel: '처리 예정', actionCls: 'bg-indigo-100 text-indigo-700', desc: '' };
    };

    const countMatch = (line: string) => {
      const m = line.match(/(\d+)개/);
      return m ? parseInt(m[1], 10) : null;
    };

    const formatCount = (count: number | null) => count !== null ? count.toLocaleString() : '-';

    const allGenerated = allStats !== null && allStats.pending === 0 && (allStats.ai + allStats.fallback) > 0;
    const newImagesLine = newImages > 0
      ? [allGenerated
          ? { label: `새로 저장된 이미지: ${newImages.toLocaleString()}개`, count: newImages, actionable: false, actionLabel: '✅ 생성 완료', actionCls: 'bg-green-100 text-green-700', desc: 'ALT 텍스트가 모두 생성되었습니다' }
          : { label: `새로 저장된 이미지 (ALT 미생성): ${newImages.toLocaleString()}개`, count: newImages, actionable: true, actionLabel: '→ ALT 생성 대상', actionCls: 'bg-indigo-600 text-white', desc: '아래 "미생성 이미지 ALT 생성" 버튼을 클릭하세요' }]
      : [];

    const rows = bulletLines.map(line => ({ ...classify(line), count: countMatch(line) }));

    return (
      <div className="mt-4 rounded-lg border bg-white overflow-hidden text-sm">
        <div className="bg-gray-50 border-b px-4 py-2">
          <p className="font-semibold text-gray-700">{titleLine}</p>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b text-xs text-gray-500">
              <th className="text-left px-4 py-2 font-medium">항목</th>
              <th className="text-right px-4 py-2 font-medium">수량</th>
              <th className="text-center px-4 py-2 font-medium">ALT 생성 여부</th>
              <th className="text-left px-4 py-2 font-medium hidden md:table-cell">설명</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-700">{row.label}</td>
                <td className="px-4 py-2 text-right font-semibold text-gray-800">{formatCount(row.count)}</td>
                <td className="px-4 py-2 text-center">
                  <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${row.actionCls}`}>{row.actionLabel}</span>
                </td>
                <td className="px-4 py-2 text-gray-500 hidden md:table-cell">{row.desc}</td>
              </tr>
            ))}
            {newImagesLine.map((row, i) => (
              <tr key={`new-${i}`} className={row.actionable ? 'bg-indigo-50' : 'bg-green-50'}>
                <td className={`px-4 py-2 font-semibold ${row.actionable ? 'text-indigo-800' : 'text-green-800'}`}>{row.label}</td>
                <td className={`px-4 py-2 text-right font-bold ${row.actionable ? 'text-indigo-800' : 'text-green-800'}`}>{formatCount(row.count)}</td>
                <td className="px-4 py-2 text-center">
                  <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${row.actionCls}`}>{row.actionLabel}</span>
                </td>
                <td className={`px-4 py-2 hidden md:table-cell ${row.actionable ? 'text-indigo-600' : 'text-green-600'}`}>{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {newImages === 0 && (
          <div className="px-4 py-3 text-xs text-blue-700 bg-blue-50 border-t">
            ℹ️ <strong>ALT 생성 대상 이미지가 없습니다.</strong> 모든 이미지가 이미 처리되었거나 생성 대상이 아닙니다.
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <PageTitle title="웹사이트 ALT 생성" />
      <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '웹사이트 크롤링'}
        description="웹사이트의 모든 이미지를 AI로 분석하여 기존 ALT와 AI 생성 ALT를 비교·제공합니다"
        icon={menuIcon}
        action={
          <Button variant="outline" onClick={() => setShowGuide(!showGuide)} className="shrink-0">
            <HelpCircle className="w-4 h-4 mr-2" />
            사용 가이드
          </Button>
        }
      />
      <div className="space-y-8">
        <Card>
          <CardContent className="space-y-6 pt-6">
            {/* AI 사용량 표시 */}
            {aiUsage && (
              <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2 rounded-lg text-sm border ${
                aiUsage.isTeamContext
                  ? (aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null && aiUsage.teamTotalUsed >= aiUsage.teamLimit)
                    ? 'bg-red-50 border-red-300'
                    : 'bg-blue-50 border-blue-200'
                  : (!aiUsage.canUse ? 'bg-red-50 border-red-300' :
                  (aiUsage.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))
                    ? 'bg-orange-50 border-orange-300'
                    : 'bg-gray-50 border-gray-200')
              }`}>
                <span className="text-gray-700">
                  {aiUsage.isTeamContext ? (
                    <>
                      {aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null ? (
                        <>
                          팀 사용량: <strong>{aiUsage.teamTotalUsed.toLocaleString()}</strong> / {aiUsage.teamLimit.toLocaleString()}건
                          <span className="ml-2 text-gray-500">(본인: {aiUsage.used.toLocaleString()}건)</span>
                        </>
                      ) : (
                        <>
                          본인 사용량: <strong>{aiUsage.used.toLocaleString()}</strong> / {aiUsage.limit.toLocaleString()}건
                        </>
                      )}
                      <span className="ml-2 text-gray-500">({aiUsage.teamName} 팀, {aiUsage.role === 'OWNER' ? '팀장' : '팀원'}, {aiUsage.plan})</span>
                    </>
                  ) : (
                    <>
                      이번 달 AI 사용량: <strong>{aiUsage.used.toLocaleString()}</strong> / {aiUsage.limit.toLocaleString()}건
                      <span className="ml-2 text-gray-500">({aiUsage.plan} 플랜입니다)</span>
                    </>
                  )}
                </span>
                {aiUsage.isTeamContext ? (
                  aiUsage.teamTotalUsed !== null && aiUsage.teamLimit !== null && aiUsage.teamTotalUsed >= aiUsage.teamLimit && (
                    <span className="text-red-600 font-semibold text-xs">⚠️ 팀 한도 초과 &mdash; 관리자에게 문의해 주세요.</span>
                  )
                ) : (
                  !aiUsage.canUse && (
                    <span className="text-red-600 font-semibold text-xs">⚠️ 한도 초과 &mdash; 관리자에게 문의해 주세요.</span>
                  )
                )}
                {!aiUsage.isTeamContext && aiUsage.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (
                  (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0) ? (
                    <span className="text-orange-600 font-semibold text-xs">
                      ⚠️ 오늘 {(aiUsage.dailyUsed ?? 0)}/{aiUsage.dailyLimit}건 &mdash; 일일 한도 초과. 내일 다시 이용하거나 플랜을 업그레이드하세요.
                    </span>
                  ) : (
                    <span className="text-gray-500 text-xs">
                      오늘 {(aiUsage.dailyUsed ?? 0)}/{aiUsage.dailyLimit}건 (무료 플랜 일일 한도)
                    </span>
                  )
                )}
              </div>
            )}

            {/* 사용 가이드 */}
            {showGuide && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-900">
                    <Info className="w-5 h-5" />
                    웹사이트 크롤링 사용 가이드
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">📌 크롤링 모드</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li><strong>단일 페이지</strong>: 입력한 URL 페이지 하나만 스캔합니다</li>
                      <li><strong>도메인 전체</strong>: 도메인 내 모든 연결된 페이지를 재귀적으로 스캔합니다</li>
                      <li>도메인 전체 모드에서는 깊이(depth)와 페이지 수 제한을 설정할 수 있습니다</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">🔧 고급 옵션 (도메인 전체 모드)</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li><strong>최대 깊이</strong>: 링크를 따라가는 최대 깊이 (기본값: 10)</li>
                      <li><strong>최대 페이지 수</strong>: 크롤링할 최대 페이지 개수 (기본값: 100)</li>
                      <li><strong>중복 제거</strong>: 이전 스캔에서 DB에 저장된 이미지는 새로 저장하지 않음 (권장)</li>
                      <li><strong>재스캔</strong>: 이전 이력 무시, 모든 이미지를 새 레코드로 저장 — 이미지가 교체되었거나 DB 초기화 후 사용</li>
                      <li><strong>프리셋</strong>: 빠른 설정 - 얕게(5/50), 보통(10/100), 깊게(20/200)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">🎯 사용 방법</h4>
                    <ol className="list-decimal pl-5 space-y-1 text-gray-700">
                      <li>크롤링 모드 선택 (단일 페이지 / 도메인 전체)</li>
                      <li>웹사이트 URL 입력 (예: https://example.com)</li>
                      <li>도메인 전체 모드인 경우 고급 옵션 설정</li>
                      <li>"스캔" 버튼 클릭하여 크롤링 시작</li>
                      <li>스캔 완료 후 "ALT 생성" 버튼으로 대량 생성</li>
                      <li>이미지 카드에서 <strong>기존 ALT</strong>와 <strong>AI 생성 ALT</strong>를 함께 비교 확인</li>
                      <li>마음에 드는 ALT를 복사하여 사이트에 적용하세요</li>
                    </ol>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">💡 팁</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li>처음에는 <strong>단일 페이지</strong>로 테스트해보세요</li>
                      <li>대규모 사이트는 최대 페이지 수를 제한하여 시작하세요</li>
                      <li>ALT 텍스트를 더블클릭하면 바로 복사됩니다</li>
                      <li>HTML 태그 버튼으로 전체 img 태그를 복사할 수 있습니다</li>
                      <li>필터 기능으로 대기/완료 상태만 볼 수 있습니다</li>
                      <li><strong>캐시 시스템</strong>: 동일 이미지는 캐시에서 빠르게 제공됩니다 (캐시 횟수 표시)</li>
                      <li><strong>강제 재분석</strong>: 캐시가 있는 경우 "강제 재분석" 버튼으로 새로 생성 가능 (AI 사용량 차감)</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">🔄 ALT 생성 타입</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      <li><Badge className="bg-green-600 text-white text-xs">AI</Badge> OpenAI로 생성된 고품질 ALT 텍스트</li>
                      <li><Badge className="bg-yellow-600 text-white text-xs">FALLBACK</Badge> Google Vision 등 대체 API로 생성</li>
                      <li><Badge className="bg-gray-600 text-white text-xs">PENDING</Badge> 아직 생성되지 않음</li>
                      <li><Badge className="bg-red-600 text-white text-xs">ERROR</Badge> 생성 오류</li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-yellow-900 text-xs">
                      <strong>⚠️ 주의:</strong> 도메인 전체 크롤링은 시간이 오래 걸릴 수 있으며, 대상 웹사이트에 부하를 줄 수 있습니다. 
                      적절한 제한값을 설정하고 사용하세요.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 크롤링 모드 라디오 버튼을 위에, URL 입력 아래에 스캔 버튼 */}
            <div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-4">
              <div className="flex gap-2 mb-1">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="crawlMode"
                    checked={crawlMode === 'SINGLE_PAGE'}
                    onChange={() => setCrawlMode('SINGLE_PAGE')}
                    disabled={scanning}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">단일 페이지</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="crawlMode"
                    checked={crawlMode === 'DOMAIN_WIDE'}
                    onChange={() => setCrawlMode('DOMAIN_WIDE')}
                    disabled={scanning}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">도메인 전체</span>
                </label>
              </div>
              <Input
                type="text"
                placeholder="https://example.com"
                value={originalUrl}
                onChange={e => handleUrlChange(e.target.value)}
                disabled={scanning}
                className="flex-1 min-w-0"
              />
              <Button
                onClick={handleScan}
                disabled={scanning || !url.trim() || isSuperAdmin || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                className="self-end md:w-auto md:ml-2"
                variant="default"
              >
                {scanning ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    스캔 중...
                  </>
                ) : (
                  '스캔'
                )}
              </Button>
            </div>

            {isSuperAdmin && (
              <Alert className="bg-amber-50 border-amber-300">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  SUPER_ADMIN 계정은 ALT 생성 기능을 사용할 수 없습니다. ALT 생성은 CLIENT 계정으로 로그인하여 이용해 주세요.
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* 고급 옵션 (도메인 전체일 때만) */}
            {crawlMode === 'DOMAIN_WIDE' && (
              <Card className="bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-base">⚙️ 고급 옵션</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">
                        깊이 제한 (1~20)
                      </label>
                      <Input
                        type="number"
                        value={maxDepth}
                        onChange={(e) => setMaxDepth(Number(e.target.value))}
                        min={1}
                        max={20}
                        disabled={scanning}
                      />
                      <small className="text-gray-600">기본값: 10, 최대: 20</small>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">
                        방문 수 제한 (1~200)
                      </label>
                      <Input
                        type="number"
                        value={maxPages}
                        onChange={(e) => setMaxPages(Number(e.target.value))}
                        min={1}
                        max={200}
                        disabled={scanning}
                      />
                      <small className="text-gray-600">기본값: 100, 최대: 200</small>
                    </div>
                  </div>
                  
                  {/* 프리셋 버튼 */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-sm font-semibold text-gray-700">📊 프리셋:</span>
                    <Button
                      onClick={() => applyPreset('shallow')}
                      variant="outline"
                      size="sm"
                      disabled={scanning}
                    >
                      얕은 스캔 (5/50)
                    </Button>
                    <Button
                      onClick={() => applyPreset('normal')}
                      variant="outline"
                      size="sm"
                      disabled={scanning}
                      className="border-green-500 text-green-700"
                    >
                      보통 스캔 ⭐ (10/100)
                    </Button>
                    <Button
                      onClick={() => applyPreset('deep')}
                      variant="outline"
                      size="sm"
                      disabled={scanning}
                    >
                      전체 스캔 (20/200)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 중복 제거 옵션 제거 - 시스템이 자동으로 COMPLETED 상태의 이미지는 중복 제거 */}

            {/* 컨텍스트 프로필 옵션 */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="useContextProfile"
                checked={useContextProfile}
                onChange={(e) => setUseContextProfile(e.target.checked)}
                disabled={!profileActive}
                className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
              />
              <label
                htmlFor="useContextProfile"
                className={`text-sm select-none ${profileActive ? 'cursor-pointer' : 'cursor-not-allowed text-gray-400'}`}
              >
                개인별 컨텍스트 프로필 적용
                {profileActive
                  ? <span className="text-xs text-gray-500 ml-1">(설정 → 컨텍스트 프로필에서 업종/사이트 유형 등 설정 가능)</span>
                  : <span className="text-xs text-amber-600 ml-1">(컨텍스트 프로필을 먼저 등록해주세요 → 프로필 메뉴)</span>
                }
              </label>
            </div>

            {scanResult && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-lg">✅ 스캔 결과</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-blue-600">
                        {scanResult.totalPages.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">방문 페이지</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-orange-600">
                        {scanResult.totalImages.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">발견 이미지</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-green-600">
                        {scanResult.newImages.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">새로 저장</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg">
                      <p className="text-3xl font-bold text-gray-600">
                        {scanResult.duplicateSkipped.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">DB 중복 제외</p>
                      <p className="text-xs text-gray-400 mt-0.5">이전 스캔 저장 이미지</p>
                    </div>
                  </div>
                  {renderScanStats(scanResult)}
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {(() => {
          const stats = getAltTypeStats();
          return (
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardHeader>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <CardTitle className="text-lg">ALT 텍스트 생성 통계
                      <span className="text-sm font-normal text-gray-500 ml-2">— 조회 결과 기준</span>
                    </CardTitle>
                    {stats.pending > 0 && !generatingProgress && !generationComplete && (
                      <p className="text-sm text-gray-500 mt-1">미생성 이미지 <strong className="text-indigo-700">{stats.pending.toLocaleString()}건</strong>에 ALT를 생성합니다</p>
                    )}
                    {generatingProgress && (
                      <p className="text-sm text-blue-600 mt-1 flex items-center gap-1">
                        <Spinner size="3.5" />
                        AI가 ALT 텍스트를 생성하고 있습니다 &mdash; 잠시 기다려 주세요
                      </p>
                    )}
                    {generationComplete && stats.pending === 0 && (
                      <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        모든 이미지의 ALT 텍스트 생성이 완료되었습니다!
                      </p>
                    )}
                  </div>
                  {stats.pending > 0 && !generatingProgress && (
                    <Button
                      onClick={() => {
                        if (aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0)) {
                          alert(`무료 플랜은 하루 최대 ${aiUsage.dailyLimit}건의 ALT 텍스트를 생성할 수 있습니다.\n제한 없이 이용하시려면 스타트 플랜 이상으로 업그레이드해 주세요.`);
                          return;
                        }
                        handleGenerateAlt();
                      }}
                      disabled={generating || isSuperAdmin || (aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                      variant="default"
                      size="sm"
                      className={(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0)) ? 'opacity-50 cursor-not-allowed' : ''}
                    >
                      {generating ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          요청 중...
                        </>
                      ) : (
                        `미생성 이미지 ALT 생성 (${stats.pending.toLocaleString()}건)`
                      )}
                    </Button>
                  )}
                  {generatingProgress && (
                    <div className="flex items-center gap-2 text-blue-600 text-sm font-medium">
                      <Spinner size="sm" />
                      <span>
                        생성중
                        {initialPendingCount > 0 && stats.pending < initialPendingCount
                          ? ` (${initialPendingCount - stats.pending}/${initialPendingCount} 완료)`
                          : ` (${stats.pending}건 남음)`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-green-600">{stats.ai.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">AI 분석</p>
                    <p className="text-xs text-green-600 font-semibold">{stats.aiPercentage}% 정확도</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-yellow-600">{stats.fallback.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">임시 생성</p>
                    <p className="text-xs text-yellow-600 font-semibold">재분석 권장</p>
                  </div>
                  <div className={`text-center p-4 rounded-lg shadow-sm transition-colors ${generatingProgress && stats.pending > 0 ? 'bg-blue-50 ring-2 ring-blue-300' : generationComplete && stats.pending === 0 ? 'bg-green-50' : 'bg-white'}`}>
                    <p className={`text-3xl font-bold transition-all ${generatingProgress && stats.pending > 0 ? 'text-blue-600' : generationComplete && stats.pending === 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      {generationComplete && stats.pending === 0 ? '✅' : stats.pending.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {generatingProgress && stats.pending > 0
                        ? <span className="text-blue-600 font-semibold flex items-center justify-center gap-1"><Spinner size="xs" />생성중...</span>
                        : generationComplete && stats.pending === 0
                          ? <span className="text-green-600 font-semibold">완료!</span>
                          : '미생성'
                      }
                    </p>
                    {generatingProgress && initialPendingCount > 0 && stats.pending < initialPendingCount && (
                      <p className="text-xs text-blue-500 mt-0.5">{initialPendingCount - stats.pending}/{initialPendingCount} 완료</p>
                    )}
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-red-600">{stats.error.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">오류</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-3xl font-bold text-purple-600">{stats.skippedDecorative.toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">장식용 이미지</p>
                    <p className="text-xs text-purple-600 font-semibold">ALT 생성 건너뜀</p>
                  </div>
                </div>
                {stats.fallback > stats.ai && stats.fallback > 0 && (
                  <Alert className="mt-4 bg-yellow-50 border-yellow-300">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">
                      <strong>⚠️ 임시 생성된 ALT 텍스트가 많습니다.</strong>
                      <br />
                      임시 생성된 항목은 이미지 URL 기반으로 만들어져 정확하지 않을 수 있습니다.
                      각 항목의 "AI 재분석" 버튼을 클릭하면 더 정확한 ALT 텍스트를 받을 수 있습니다.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          );
        })()}

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle className="text-xl">이미지 목록({totalElements.toLocaleString()}건)</CardTitle>
              <div className="flex gap-2 w-full md:w-auto flex-wrap">
                {images.length > 0 && (
                  <>
                    <Button
                      onClick={handleExcelDownload}
                      disabled={excelLoading}
                      variant="outline"
                      size="sm"
                      className="text-green-700 border-green-400 hover:bg-green-50"
                    >
                      {excelLoading ? (
                        <><Spinner size="sm" className="mr-2" />다운로드 중...</>
                      ) : (
                        <><FileDown className="w-4 h-4 mr-2" />엑셀 다운로드 ({totalElements.toLocaleString()}건)</>
                      )}
                    </Button>
                    <Button
                      onClick={handleClearAll}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      전체 삭제
                    </Button>
                    {viewMode === 'website' && selectedWebsite && (
                      <Button
                        onClick={handleClearWebsite}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        선택 삭제
                      </Button>
                    )}
                  </>
                )}
                <Button
                  onClick={() => { setAltTypeFilter('ALL'); fetchImages(0, filter === 'ALL' ? null : filter, viewMode === 'website' ? selectedWebsite : null, pageSize, undefined, undefined, 'ALL'); }}
                  variant="outline"
                  size="sm"
                  className={altTypeFilter === 'ALL' ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' : ''}
                >
                  전체
                </Button>
                <Button
                  onClick={() => { setAltTypeFilter('AI'); fetchImages(0, filter === 'ALL' ? null : filter, viewMode === 'website' ? selectedWebsite : null, pageSize, undefined, undefined, 'AI'); }}
                  variant="outline"
                  size="sm"
                  className={altTypeFilter === 'AI' ? 'bg-green-600 text-white hover:bg-green-700 border-green-600' : ''}
                >
                  ✓ AI 분석
                </Button>
                <Button
                  onClick={() => { setAltTypeFilter('FALLBACK'); fetchImages(0, filter === 'ALL' ? null : filter, viewMode === 'website' ? selectedWebsite : null, pageSize, undefined, undefined, 'FALLBACK'); }}
                  variant="outline"
                  size="sm"
                  className={altTypeFilter === 'FALLBACK' ? 'bg-yellow-600 text-white hover:bg-yellow-700 border-yellow-600' : ''}
                >
                  ⚠ 임시 생성
                </Button>
                <Button
                  onClick={() => { setAltTypeFilter('PENDING'); fetchImages(0, filter === 'ALL' ? null : filter, viewMode === 'website' ? selectedWebsite : null, pageSize, undefined, undefined, 'PENDING'); }}
                  variant="outline"
                  size="sm"
                  className={altTypeFilter === 'PENDING' ? 'bg-gray-600 text-white hover:bg-gray-700 border-gray-600' : ''}
                >
                  📝 미생성
                </Button>
                <Button
                  onClick={() => { setAltTypeFilter('ERROR'); fetchImages(0, filter === 'ALL' ? null : filter, viewMode === 'website' ? selectedWebsite : null, pageSize, undefined, undefined, 'ERROR'); }}
                  variant="outline"
                  size="sm"
                  className={altTypeFilter === 'ERROR' ? 'bg-red-600 text-white hover:bg-red-700 border-red-600' : ''}
                >
                  ✗ 오류
                </Button>
                <Button
                  onClick={() => { setAltTypeFilter('SKIPPED_DECORATIVE'); fetchImages(0, filter === 'ALL' ? null : filter, viewMode === 'website' ? selectedWebsite : null, pageSize, undefined, undefined, 'SKIPPED_DECORATIVE'); }}
                  variant="outline"
                  size="sm"
                  className={altTypeFilter === 'SKIPPED_DECORATIVE' ? 'bg-purple-600 text-white hover:bg-purple-700 border-purple-600' : ''}
                >
                  🎨 장식용
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">ℹ️ 이미지를 삭제해도 AI 분석 완료 건수는 월별 사용량에 포함됩니다.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start md:items-center">
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setViewMode('all');
                      setSelectedWebsite('');
                    }}
                    variant="outline"
                    size="sm"
                    className={viewMode === 'all' ? 'bg-green-600 text-white hover:bg-green-700 border-green-600' : ''}
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    전체
                  </Button>
                  {images.length > 0 && (
                    <Button
                      onClick={() => setViewMode('website')}
                      variant="outline"
                      size="sm"
                      className={viewMode === 'website' ? 'bg-green-600 text-white hover:bg-green-700 border-green-600' : ''}
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      웹페이지별
                    </Button>
                  )}
                </div>
                
                {viewMode === 'website' && (
                  <select
                    value={selectedWebsite}
                    onChange={(e) => setSelectedWebsite(e.target.value)}
                    className="flex h-9 w-full md:w-auto md:min-w-[150px] rounded-md border border-input bg-background px-2 py-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">웹페이지 선택</option>
                    {websites.map((website, index) => (
                      <option key={index} value={website}>
                        {website}
                      </option>
                    ))}
                  </select>
                )}

                <div className="flex items-center gap-2 w-full md:w-auto">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="h-9 w-full md:w-auto text-xs"
                    />
                    <span className="text-gray-500 text-xs">~</span>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="h-9 w-full md:w-auto text-xs"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSearch}
                  variant="default"
                  size="sm"
                  className="w-full md:w-auto md:ml-2"
                >
                  <Search className="w-4 h-4 mr-2" />
                  조회
                </Button>
              </div>

              <Tabs value={filter} onValueChange={(v) => {
                const newFilter = v as 'ALL' | 'PENDING' | 'COMPLETED';
                setFilter(newFilter);
                const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
                const statusFilter = newFilter === 'ALL' ? null : newFilter;
                fetchImages(0, statusFilter, websiteFilter);
              }} className="w-full">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                  <TabsList className="grid grid-cols-3 flex-1">
                    <TabsTrigger value="ALL">전체</TabsTrigger>
                    <TabsTrigger value="PENDING">대기</TabsTrigger>
                    <TabsTrigger value="COMPLETED">완료</TabsTrigger>
                  </TabsList>
                  <div className="flex items-center gap-1 shrink-0">
                    <Select
                      value={`${sortBy}_${sortDir}`}
                      onValueChange={(v) => {
                        const lastIdx = v.lastIndexOf('_');
                        const newSortBy = v.substring(0, lastIdx) as SortBy;
                        const newSortDir = v.substring(lastIdx + 1) as SortDir;
                        setSortBy(newSortBy);
                        setSortDir(newSortDir);
                        const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
                        fetchImages(0, filter === 'ALL' ? null : filter, websiteFilter, pageSize, newSortBy, newSortDir);
                      }}
                    >
                      <SelectTrigger className="w-[180px] h-9 text-sm">
                        <SelectValue placeholder="정렬" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CREATED_AT_DESC">등록일 최신순{sortIsDefault && sortBy === 'CREATED_AT' && sortDir === 'DESC' ? ' (기본)' : ''}</SelectItem>
                        <SelectItem value="CREATED_AT_ASC">등록일 오래된순</SelectItem>
                        <SelectItem value="UPDATED_AT_DESC">수정일 최신순</SelectItem>
                        <SelectItem value="UPDATED_AT_ASC">수정일 오래된순</SelectItem>
                        <SelectItem value="STATUS_ASC">상태순</SelectItem>
                        <SelectItem value="URL_NORMALIZED_ASC">URL 오름차순</SelectItem>
                        <SelectItem value="URL_NORMALIZED_DESC">URL 내림차순</SelectItem>
                        <SelectItem value="COMPLETION_DESC">완료율 높은순</SelectItem>
                        <SelectItem value="COMPLETION_ASC">완료율 낮은순</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <TabsContent value={filter} className="mt-6">
                  {loading ? (
                    <LoadingBlock size="4xl" py={12} iconClassName="text-blue-500" label="데이터를 불러오는 중..." labelPosition="below" />
                  ) : filteredImages.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>{images.length === 0 ? '아직 처리된 이미지가 없습니다' : '해당 조건의 이미지가 없습니다'}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredImages.map((img) => (
                        <Card key={img.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex flex-col md:flex-row gap-4">
                              <img
                                src={img.displayImageUrl ?? img.imageSrc}
                                alt={img.generatedAlt || img.originalAlt || ''}
                                className="w-full md:w-32 h-32 object-cover rounded-lg flex-shrink-0"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="128" height="128"%3E%3Crect fill="%23ddd" width="128" height="128"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                  // 브라우저에서도 로드 실패 → 진짜 없는 이미지
                                  setBrowserFailedImages(prev => new Set(Array.from(prev).concat(img.id)));
                                }}
                              />
                              <div className="flex-1 space-y-2 min-w-0">
                                {/* 배지 영역 */}
                                <div className="flex gap-2 flex-wrap">
                                  {getStatusBadge(img.status)}
                                  {getAltTypeBadge(img.altGenerationType)}
                                  {img.originalAlt !== null && img.originalAlt !== undefined ? (
                                    img.originalAlt === '' ? (
                                      <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs">기존 ALT: 빈값</Badge>
                                    ) : (
                                      <Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">기존 ALT 있음 ✓</Badge>
                                    )
                                  ) : (
                                    <Badge className="bg-red-100 text-red-700 border border-red-200 text-xs">ALT 누락 ⚠</Badge>
                                  )}
                                  {img.cachecnt !== undefined && (
                                    <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs">
                                      캐시 {img.cachecnt}회
                                    </Badge>
                                  )}
                                  {img.altApprovalStatus === 'PENDING' && (
                                    <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200 text-xs">⏳ 검수대기</Badge>
                                  )}
                                  {img.altApprovalStatus === 'APPROVED' && (
                                    <Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">✅ 승인됨</Badge>
                                  )}
                                  {img.altApprovalStatus === 'REJECTED' && (
                                    <Badge className="bg-red-100 text-red-700 border border-red-200 text-xs">❌ 거부됨</Badge>
                                  )}
                                </div>

                                {/* 이미지 URL */}
                                <div>
                                  <p className="text-sm text-gray-600 break-all">
                                    <span className="font-semibold">이미지 URL:</span> {img.imageSrc}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {img.updatedAt ? '최종 수정' : '등록'}: {formatRelativeTime(img.updatedAt || img.createdAt)} ({formatDateTime(img.updatedAt || img.createdAt)})
                                  </p>
                                </div>

                                {/* ALT 비교 영역 */}
                                <div className={`rounded-lg border p-3 ${
                                  img.altGenerationType === 'AI' ? 'bg-green-50 border-green-200' :
                                  img.altGenerationType === 'FALLBACK' ? 'bg-yellow-50 border-yellow-200' :
                                  img.altGenerationType === 'ERROR' ? 'bg-red-50 border-red-200' :
                                  isLongPending(img.createdAt) ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
                                }`}>
                                  <div className="flex flex-col gap-3">
                                    {/* 기존 ALT */}
                                    {img.originalAlt !== null && img.originalAlt !== undefined && (
                                      <div>
                                        <p className="text-xs font-semibold text-gray-500 mb-1">📄 기존 ALT</p>
                                        <p className="text-sm text-gray-700 p-2 rounded bg-white/50 break-words">
                                          {img.originalAlt || '(빈값)'}
                                        </p>
                                      </div>
                                    )}
                                    {/* AI 생성 ALT */}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-gray-500 mb-1">🤖 AI 생성 ALT</p>
                                    {img.generatedAlt ? (
                                      <>
                                        <p
                                          className={`text-sm mb-1 p-2 rounded transition-colors ${
                                            img.altGenerationType === 'AI' ? 'font-semibold' : ''
                                          } ${
                                            img.altGenerationType === 'AI' ? 'text-green-800' :
                                            img.altGenerationType === 'FALLBACK' ? 'text-yellow-800' :
                                            img.altGenerationType === 'ERROR' ? 'text-red-600' :
                                            'text-gray-800'
                                          } ${
                                            img.altGenerationType !== 'ERROR' ? 'cursor-pointer hover:bg-white/50' : ''
                                          }`}
                                          {...(img.altGenerationType !== 'ERROR' && {
                                            onDoubleClick: () => handleDoubleClick(img.generatedAlt!),
                                            title: '더블클릭하여 복사'
                                          })}
                                        >
                                          {img.altGenerationType === 'ERROR'
                                            ? (() => {
                                                // 브라우저에서도 이미지 로드 실패하면 → 실제로 없는 이미지
                                                const errType = browserFailedImages.has(img.id)
                                                  ? 'IMAGE_NOT_FOUND'
                                                  : classifyImageError(img.generatedAlt, img.imageSrc);
                                                if (errType === 'CDN_BLOCKED') {
                                                  return (
                                                    <span className="block space-y-1">
                                                      <span className="block font-semibold">🔒 AI 서버의 이미지 접근이 차단되었습니다</span>
                                                      <span className="block text-xs text-red-700 leading-relaxed">
                                                        이 이미지는 <strong>화면에서는 정상적으로 보이지만</strong>, Kakao CDN의 보안 정책(Signed URL)에 의해 AI 분석 서버에서의 직접 접근이 차단됩니다.
                                                      </span>
                                                      <span className="block text-xs text-gray-500 leading-relaxed">
                                                        💡 <strong>왜 내 PC에서는 보이나요?</strong> 브라우저는 원본 블로그 페이지를 통해 이미지를 로드하기 때문에 허용되지만, AI 서버는 외부에서 직접 요청하므로 CDN이 차단합니다. 원본 사이트가 아닌 곳에서의 접근은 CDN 보안 정책상 일괄 거부됩니다.
                                                      </span>
                                                      <span className="block text-xs text-orange-700 font-medium">
                                                        → 이미지를 PC에 저장 후{' '}
                                                        <span
                                                          className="underline cursor-pointer hover:text-red-800 font-semibold"
                                                          onClick={() => navigate('/dashboard/crawler/upload')}
                                                          title="이미지 업로드 페이지로 이동"
                                                        >이미지 업로드</span>{' '}메뉴에서 직접 업로드하면 분석할 수 있습니다.
                                                      </span>
                                                    </span>
                                                  );
                                                }
                                                if (errType === 'IMAGE_NOT_FOUND') {
                                                  return <span>이미지를 불러올 수 없습니다.</span>;
                                                }
                                                // OTHER: 원본 메시지 표시 (이미지 업로드 링크 처리 포함)
                                                return img.generatedAlt?.includes('이미지 업로드')
                                                  ? img.generatedAlt!.split('이미지 업로드').map((part, i, arr) =>
                                                      i < arr.length - 1
                                                        ? <React.Fragment key={i}>{part}<span
                                                            className="underline cursor-pointer hover:text-red-800 font-semibold"
                                                            onClick={() => navigate('/dashboard/crawler/upload')}
                                                            title="이미지 업로드 페이지로 이동"
                                                          >이미지 업로드</span></React.Fragment>
                                                        : <React.Fragment key={i}>{part}</React.Fragment>
                                                    )
                                                  : img.generatedAlt;
                                              })()
                                            : img.generatedAlt
                                          }
                                        </p>
                                        {img.altGenerationType === 'FALLBACK' && (
                                          <p className="text-xs text-yellow-700 mt-2 italic">💡 이 설명은 URL 기반으로 임시 생성되었습니다. 정확하지 않을 수 있습니다.</p>
                                        )}
                                      </>
                                    ) : (
                                      isLongPending(img.createdAt) ? (
                                        <div>
                                          <p className="text-sm text-amber-800 font-medium">⚠️ AI 분석이 자동으로 완료되지 않았습니다.</p>
                                          <p className="text-xs text-amber-700 mt-1">아래 버튼을 눌러 직접 ALT를 생성해 주세요.</p>
                                          <Button
                                            onClick={() => handleRegenerateAlt(img.id)}
                                            size="sm"
                                            variant="outline"
                                            className="mt-2 text-amber-700 border-amber-400 hover:bg-amber-100 whitespace-nowrap"
                                            disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                          >
                                            {regeneratingIds.has(img.id)
                                              ? <><Spinner size="xs" className="mr-1" />분석중...</>
                                              : <>🔄 ALT 생성</>}
                                          </Button>
                                        </div>
                                      ) : (
                                        <div>
                                          <p className="text-sm text-blue-800 font-medium">⏳ AI가 ALT 텍스트를 분석 중입니다.</p>
                                          <p className="text-xs text-blue-600 mt-1">잠시 후 자동으로 결과가 표시됩니다. (약 10초 소요)</p>
                                        </div>
                                      )
                                    )}
                                    </div>
                                    </div>
                                    {/* 커스텀 ALT 섹션 */}
                                    <div className="mt-4">
                                      <p className="text-xs font-semibold text-gray-500 mb-2">✏️ 커스텀 ALT</p>
                                      <CustomAltEditor
                                        imageId={img.id}
                                        customAltText={img.customAltText}
                                        onSaved={(customAltText) => handleCustomAltSaved(img.id, customAltText)}
                                        onDeleted={() => handleCustomAltDeleted(img.id)}
                                      />
                                    </div>
                                    <div className="flex flex-wrap gap-2 shrink-0">
                                      {img.generatedAlt && img.altGenerationType !== 'ERROR' && (
                                        <>
                                          <Button
                                            onClick={() => handleCopyAlt(img.generatedAlt!, img.id)}
                                            size="sm"
                                            variant="outline"
                                            className="whitespace-nowrap"
                                          >
                                            {copiedId === img.id ? (
                                              <><Check className="w-3 h-3 mr-1 text-green-600" />복사됨</>
                                            ) : (
                                              <><Copy className="w-3 h-3 mr-1" />AI ALT 복사</>
                                            )}
                                          </Button>
                                          <Button
                                            onClick={() => handleCopyHtml(img)}
                                            size="sm"
                                            variant="outline"
                                            className="whitespace-nowrap"
                                          >
                                            {copiedHtmlId === img.id ? (
                                              <><Check className="w-3 h-3 mr-1 text-green-600" />복사됨</>
                                            ) : (
                                              <><Copy className="w-3 h-3 mr-1" />HTML 복사</>
                                            )}
                                          </Button>
                                        </>
                                      )}
                                      {img.status !== 'COMPLETED' && (!img.cachecnt || img.cachecnt === 0) && (
                                        <Button
                                          onClick={() => handleRegenerateAlt(img.id)}
                                          variant="outline"
                                          size="sm"
                                          className="text-blue-600 border-blue-300 hover:bg-blue-50 whitespace-nowrap"
                                          disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                        >
                                          {regeneratingIds.has(img.id) ? (
                                            <><Spinner size="sm" className="mr-1" />재분석 중...</>
                                          ) : (
                                            <><RefreshCw className="w-4 h-4 mr-1" />재분석</>
                                          )}
                                        </Button>
                                      )}
                                      {img.cachecnt !== undefined && img.cachecnt > 0 && (
                                        <Button
                                          onClick={() => handleRegenerateAlt(img.id, true)}
                                          variant="outline"
                                          size="sm"
                                          className="text-purple-600 border-purple-300 hover:bg-purple-50 whitespace-nowrap"
                                          disabled={regeneratingIds.has(img.id) || !!(aiUsage?.plan === 'FREE' && aiUsage.dailyLimit !== undefined && (aiUsage.dailyUsed ?? 0) >= (aiUsage.dailyLimit ?? 0))}
                                        >
                                          {regeneratingIds.has(img.id) ? (
                                            <><Spinner size="sm" className="mr-1" />강제 재분석 중...</>
                                          ) : (
                                            <><Loader2 className="w-4 h-4 mr-1" />강제 재분석</>
                                          )}
                                        </Button>
                                      )}
                                      <Button
                                        onClick={() => handleDeleteImage(img.id)}
                                        variant="destructive"
                                        size="sm"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                  {images.length > 0 && (
                    <div className="flex justify-between items-center mt-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>페이지당</span>
                        <Select
                          value={String(pageSize)}
                          onValueChange={(v) => {
                            const newSize = Number(v);
                            setPageSize(newSize);
                            setCurrentPage(0);
                            const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
                            fetchImages(0, filter === 'ALL' ? null : filter, websiteFilter, newSize);
                          }}
                        >
                          <SelectTrigger className="w-20 h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10개</SelectItem>
                            <SelectItem value="20">20개</SelectItem>
                            <SelectItem value="50">50개</SelectItem>
                            <SelectItem value="100">100개</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => {
                              const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
                              fetchImages(currentPage - 1, filter === 'ALL' ? null : filter, websiteFilter);
                            }}
                            disabled={currentPage === 0}
                            variant="outline"
                          >
                            이전
                          </Button>
                          <span className="px-4 py-2 text-sm">
                            {currentPage + 1} / {totalPages}
                          </span>
                          <Button
                            onClick={() => {
                              const websiteFilter = viewMode === 'website' ? selectedWebsite : null;
                              fetchImages(currentPage + 1, filter === 'ALL' ? null : filter, websiteFilter);
                            }}
                            disabled={currentPage >= totalPages - 1}
                            variant="outline"
                          >
                            다음
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
    </>
  );
}

export default AlttextUrl;